import classNames from "classnames";
import * as React from "react";

import { useAuth } from "@/providers";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord, ViewVisibility } from "@/shared/domain/views";
import { Save } from "@/shared/foundation/icons";
import {
  Popover,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";

import type { ResourceView } from "../useResourceView";

import styles from "./SaveViewControl.module.css";
import { overwriteViewErrorMessage } from "./saveViewError";
import type { LoadedView } from "./useLoadedView";
import { useSaveView, type SaveView } from "./useSaveView";

export interface SaveViewControlProps<TData> {
  /** The live resource view whose state is being saved. */
  view: ResourceView<TData>;
  /** Resource type the new View is written against. */
  target: QueryTarget;
  /**
   * The loaded-View session from `useLoadedView`.
   *
   * Passed whole rather than as individual callbacks: saving, discarding and
   * reading what is loaded all have to go through one owner, or they cannot
   * order themselves against each other. Discard applies a View and must share
   * the picker's applier; a save must be judged on completion by the same hook
   * that knows what changed while it was out.
   */
  session: LoadedView;
}

/**
 * Flag gate and wiring for {@link SaveViewMenu}.
 *
 * A wrapper rather than an early return, for the same reason as `ViewPicker`:
 * `useSaveView` calls mutation hooks that reach for `ViewService` whether or
 * not anything renders, so not mounting this is what prevents it.
 */
export function SaveViewControl<TData>({
  view,
  target,
  session,
}: SaveViewControlProps<TData>) {
  const viewsEnabled = useCheckFeatureFlag("release-views");

  if (!viewsEnabled) {
    return null;
  }

  return <SaveViewControlInner view={view} target={target} session={session} />;
}

function SaveViewControlInner<TData>({
  view,
  target,
  session,
}: SaveViewControlProps<TData>) {
  const save = useSaveView({ view, target, session });

  return (
    <SaveViewMenu
      loadedView={session.loadedView}
      save={save}
      onReset={session.discardChanges}
    />
  );
}

export interface SaveViewMenuProps {
  /** The View currently loaded, or `null` when the filters are ad-hoc. */
  loadedView: ViewRecord | null;
  /**
   * Saving surface from `useSaveView`. Both saves mark their own result loaded;
   * this component's job ends at dismissing the popover.
   */
  save: SaveView;
  /** Throw away the edits and put the loaded View's saved state back. */
  onReset: () => void;
}

/**
 * Save the current filters as a View, or back to the one that is loaded.
 *
 * Sits in the active-filters bar, which only exists while filters do — so this
 * needs no empty-state rule of its own.
 *
 * With a View loaded and changed, "Save" is ambiguous, so the button opens a
 * menu naming both intents: overwrite, or fork. With nothing loaded it opens
 * the form directly rather than offering a menu of one.
 *
 * Discard changes is the third, offered only with a View loaded. It is the
 * opposite of the picker's reset row and the bar's Clear, which unload the View
 * and empty the table; this keeps the View and restores its saved state.
 */
export function SaveViewMenu({ loadedView, save, onReset }: SaveViewMenuProps) {
  const { currentOrganization } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<"menu" | "form">("menu");
  const [name, setName] = React.useState("");
  const [visibility, setVisibility] = React.useState<ViewVisibility>("private");
  const [error, setError] = React.useState<string | null>(null);

  const canOverwrite = loadedView !== null && save.isDirty;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        // A half-typed name from a dismissed attempt reappearing reads as a bug.
        setMode(canOverwrite ? "menu" : "form");
        setName("");
        setVisibility("private");
        setError(null);
      }
    },
    [canOverwrite],
  );

  const handleCreate = React.useCallback(async () => {
    setError(null);
    try {
      await save.createView({ name: name.trim(), visibility });
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save.");
    }
  }, [name, save, visibility]);

  const handleOverwrite = React.useCallback(async () => {
    setError(null);
    try {
      await save.updateLoadedView();
      setOpen(false);
    } catch (caught) {
      setError(overwriteViewErrorMessage(caught));
    }
  }, [save]);

  // Nothing to save: the View on screen is the View on the server. Only when
  // one is loaded — without it there is nothing to measure against, and this is
  // the only route to a first View.
  if (loadedView !== null && !save.isDirty) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        render={
          <RobotoButton
            variant="secondary"
            icon={<Save size={15} aria-hidden />}
            disabled={save.isSaving}
          >
            Save
          </RobotoButton>
        }
      />
      <Popover.Content align="end" aria-label="Save as View">
        {mode === "menu" ? (
          <div className={styles.menu}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => void handleOverwrite()}
            >
              Save to this View
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => setMode("form")}
            >
              Create new View&hellip;
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                setOpen(false);
                onReset();
              }}
            >
              Discard changes
            </button>
            {error !== null && (
              <p className={classNames(styles.error, styles.menuError)}>
                {error}
              </p>
            )}
          </div>
        ) : (
          // A real form, so Enter in the name field submits. The browser only
          // submits implicitly while the submit button is enabled, which keeps
          // the empty-name rule in one place.
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault();
              void handleCreate();
            }}
          >
            {/*
              The only place the word "View" appears before one exists: the
              picker is in the toolbar and this control is in the filter bar,
              so a customer reaches it by saving filters, not by looking for
              Views. Naming the noun here is what connects the two.
          */}
            <h2 className={styles.formHeader}>Save as new View</h2>

            <label className={styles.label} htmlFor="save-view-name">
              Name
            </label>
            <TextInput
              id="save-view-name"
              className={styles.nameInput}
              value={name}
              placeholder="e.g. Large uploads this week"
              autoFocus
              // Chrome's history suggestions would cover the placeholder.
              autoComplete="off"
              onChange={(event) => setName(event.target.value)}
            />

            <fieldset className={styles.visibilityGroup}>
              <legend className={styles.label}>Who can access it</legend>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="save-view-visibility"
                  checked={visibility === "private"}
                  onChange={() => setVisibility("private")}
                />
                Only me
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="save-view-visibility"
                  checked={visibility === "organization"}
                  onChange={() => setVisibility("organization")}
                />
                {/*
                  Named, not "my org": a customer in several orgs cannot tell
                  which one they are about to share with otherwise. The
                  fallback is only reachable before auth resolves, which this
                  control is not normally rendered without.
              */}
                Everyone in {currentOrganization?.name ?? "my organization"}
              </label>
            </fieldset>

            {error !== null && <p className={styles.error}>{error}</p>}

            <div className={styles.actions}>
              <RobotoButton
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
              >
                Cancel
              </RobotoButton>
              <RobotoButton
                type="submit"
                variant="primary"
                // Non-empty is the only rule: names are not unique (ENG-2876).
                disabled={name.trim().length === 0 || save.isSaving}
              >
                Save
              </RobotoButton>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover>
  );
}
